<template>
  <div class="my-orders-page">
    <a-spin :spinning="processingRetry" tip="執行中..." size="large" :wrapperClassName="processingRetry ? 'processing-spin-wrapper' : ''">
    <div class="page-container">
      <!-- 頂部導航 -->
      <div class="top-navigation">
        <a-button type="link" v-if="viewname!=='Myline'" @click="goHome" class="back-button">
          <a-icon type="left" />
          返回首頁
        </a-button>
        <h1 class="page-title">🪙 雲林幣兌換學分記錄</h1>
        <div class="nav-spacer"></div>
      </div>

      <!-- 主要內容區域 -->
      <div class="content-wrapper">
        <!-- 篩選器 -->
        <div class="filter-section">
          <a-card :bordered="false" class="filter-card">
            <div class="filter-controls">
              <a-select 
                v-model="filters.status" 
                placeholder="選擇兌換狀態"
                style="width: 200px"
                @change="handleFilterChange"
              >
                <a-select-option value="">全部狀態</a-select-option>
                <a-select-option value="Y">✅ 兌換成功</a-select-option>
                <a-select-option value="N">❌ 兌換失敗</a-select-option>
              </a-select>
              
              <a-input
                v-model="filters.searchText"
                placeholder="搜尋活動名稱或單號"
                style="width: 300px"
                @pressEnter="handleFilterChange"
              >
                <a-icon slot="prefix" type="search" />
              </a-input>
              
              <a-button type="primary" @click="handleFilterChange">
                <a-icon type="search" />
                搜尋
              </a-button>
              
              <a-button @click="clearFilters">
                <a-icon type="clear" />
                清除
              </a-button>
            </div>
          </a-card>
        </div>

        <!-- 載入狀態 -->
        <div v-if="loading" class="loading-container">
          <a-card :bordered="false" style="text-align: center; padding: 80px 20px; background: linear-gradient(135deg, #f0f9ff, #ffffff); border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06);">
            <div style="margin-bottom: 24px;">
              <a-spin size="large" style="color: #1890ff;" />
            </div>
            <div style="font-size: 48px; margin-bottom: 20px;">💰</div>
            <h3 style="color: #1890ff; margin-bottom: 12px; font-size: 20px; font-weight: 600;">載入中...</h3>
            <p style="color: #64748b; font-size: 16px; margin: 0;">🔄 正在為您載入雲林幣兌換記錄，請稍候</p>
          </a-card>
        </div>

        <!-- 無訂單狀態 -->
        <div v-else-if="!orders.length && !loading" class="empty-container">
          <a-card :bordered="false" style="text-align: center; padding: 60px 20px; background: linear-gradient(135deg, #f8fafc, #ffffff); border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06);">
            <div style="font-size: 64px; margin-bottom: 24px;">
              {{ hasFilters ? '🔍' : '💰' }}
            </div>
            <h3 style="color: #334155; margin-bottom: 12px; font-size: 22px; font-weight: 600;">
              {{ hasFilters ? '沒有找到符合條件的記錄' : '暫無兌換記錄' }}
            </h3>
            <p style="color: #64748b; font-size: 16px; margin-bottom: 32px; line-height: 1.6;">
              {{ hasFilters ? '🔎 請嘗試調整搜尋條件，或清除篩選重新查看所有記錄' : '🌟 開始您的第一次雲林幣兌換之旅吧！參與活動即可獲得學分獎勵' }}
            </p>
            <div style="display: flex; justify-content: center; gap: 12px; flex-wrap: wrap;">
              <a-button 
                v-if="hasFilters" 
                type="primary" 
                size="large"
                @click="clearFilters"
                style="background: linear-gradient(135deg, #1890ff, #40a9ff); border: none; box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4); border-radius: 8px; padding: 0 32px; height: 44px;"
              >
                🗑️ 清除篩選
              </a-button>
              <a-button 
                size="large"
                @click="fetchOrders"
                style="border-radius: 8px; padding: 0 32px; height: 44px; border-color: #d1d5db; color: #374151;"
              >
                🔄 重新載入
              </a-button>
            </div>
          </a-card>
        </div>

        <!-- 訂單列表 -->
        <div v-else class="orders-container">
          <!-- 統計資訊 -->
          <div class="statistics-card">
            <a-card :bordered="false">
              <div class="stats-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px solid #f0f0f0;">
                <h3 style="margin: 0; color: #1890ff; font-size: 18px; font-weight: 600;">
                  📊 雲林幣兌換統計總覽
                </h3>
                <div style="display: flex; gap: 8px; align-items: center;">
                  <a-button 
                    v-if="hasFailedOrders"
                    type="danger" 
                    size="small" 
                    icon="reload"
                    :disabled="processingRetry"
                    @click="retryAllFailedExchanges"
                    style="background: linear-gradient(135deg, #ff4d4f, #ff7875); border: none; box-shadow: 0 2px 8px rgba(255, 77, 79, 0.3);"
                  >
                    全部重新兌換
                  </a-button>
                  <a-button 
                    type="primary" 
                    size="small" 
                    icon="bar-chart"
                    @click="showStatsDetail"
                    style="background: linear-gradient(135deg, #1890ff, #40a9ff); border: none; box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);"
                  >
                    詳細統計
                  </a-button>
                </div>
              </div>
              <div class="stats-grid">
                <div class="stat-item">
                  <div class="stat-value">{{ totalUserStats.count }}</div>
                  <div class="stat-label">總兌換次數</div>
                  <div class="stat-sublabel">{{ totalUserStats.total_point }} 總學分</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value success-stat">{{ successExchanges }}</div>
                  <div class="stat-label">兌換成功</div>
                  <div class="stat-sublabel">{{ statsData.success_stats.total_point }} 成功學分</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value failed-stat">{{ failedExchanges }}</div>
                  <div class="stat-label">兌換失敗</div>
                  <div class="stat-sublabel">{{ statsData.fail_stats.total_point }} 失敗學分</div>
                </div>
                <div class="stat-item" v-if="tradeNoStats.count > 0">
                  <div class="stat-value search-stat">{{ tradeNoStats.count }}</div>
                  <div class="stat-label">搜尋結果</div>
                  <div class="stat-sublabel">{{ tradeNoStats.total_point }} 搜尋學分</div>
                </div>
                <div class="stat-item" v-else>
                  <div class="stat-value points-stat">{{ totalPoints }}</div>
                  <div class="stat-label">已獲得學分</div>
                  <div class="stat-sublabel">{{ ((totalPoints / (totalUserStats.total_point || 1)) * 100).toFixed(1) }}% 成功率</div>
                </div>
              </div>
            </a-card>
          </div>

          <!-- 訂單卡片列表 -->
          <div class="orders-list">
            <div 
              v-for="(order, index) in paginatedOrders" 
              :key="order.id || index"
              class="order-card"
              :class="{ 'animate-in': true }"
              :style="{ 'animation-delay': index * 0.1 + 's' }"
            >
              <a-card :bordered="false" class="order-item">
                <div class="order-header">
                  <div class="order-info">
                    <h3 class="order-title">🎓 {{ order.actitle || '活動名稱未知' }}</h3>
                    <div class="order-meta order-meta-left">
                      <span class="order-number">兌換單號：{{ order.merchant_trade_no }}</span>
                      <span class="order-number">活動日期：{{ fdateformat(order.classtime) }}</span>
                      <span class="order-number" v-if="fdateformat(order.tradedate)">兌換日期：{{ fdateformat(order.tradedate) }}</span>
                      <span class="order-number" v-else>兌換日期：待處理</span>
                    </div>
                  </div>
                  <div class="order-status">
                    <div class="points-badge">
                      <span class="points-text">🪙 {{ order.orderpoint }} 學分</span>
                    </div>
                    <a-tag :color="getExchangeStatusColor(order.states)" size="large" class="exchange-status">
                      {{ getExchangeStatusText(order.states) }}
                    </a-tag>
                  </div>
                </div>

                <a-divider />

                <div class="order-content">
                  <div class="order-details">
                    <div class="detail-row">
                      <span class="detail-label">📅 活動日期：</span>
                      <span class="detail-value">{{ fdateformat(order.classtime) }}</span>
                    </div>
                    <div class="detail-row" v-if="order.fid>0">
                      <span class="detail-label">🏢 活動編號：</span>
                      <span class="detail-value">{{ order.fid }}</span>
                    </div>
                    <div class="detail-row" v-if="order.aid>0">
                      <span class="detail-label">📋 課程編號：</span>
                      <span class="detail-value">{{ order.aid }}</span>
                    </div>

                    <div class="detail-row">
                      <span class="detail-label">⏰ 兌換時間：</span>
                      <span class="detail-value">{{ fdateformat(order.tradedate) || '待處理' }}</span>
                    </div>
                    <div v-if="order.error_message && order.states === 'N'" class="detail-row error-message">
                      <span class="detail-label">❌ 失敗原因：</span>
                      <div class="detail-value error-text-wrapper">
                        <span class="error-text" v-html="order.error_message.replace(/\n/g, '<br/>')"></span>
                      </div>
                    </div>
                  </div>

                  <div class="exchange-summary">
                    <div class="summary-card" :class="{ 'success-card': order.states === 'Y', 'failed-card': order.states === 'N' }">
                      <div class="summary-icon">
                        {{ order.states === 'Y' ? '🎉' : '⚠️' }}
                      </div>
                      <div class="summary-info">
                        <div class="summary-status">
                          {{ order.states === 'Y' ? '兌換成功' : '兌換失敗' }}
                        </div>
                        <div class="summary-points">
                          {{ order.states === 'Y' ? `+${order.orderpoint}` : '0' }} 學分
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 操作按鈕 -->
                <div class="order-actions" v-if="order.states === 'N' || true">
                  <a-button 
                    class="action-btn action-btn--detail" 
                    size="small"
                    @click="viewExchangeDetail(order)"
                  >
                    <a-icon type="eye" />
                    查看詳情
                  </a-button>
                  
                  <a-button 
                    v-if="order.states === 'N'"
                    class="action-btn action-btn--retry" 
                    size="small"
                    :disabled="processingRetry"
                    @click="retrySingleExchange(order)"
                  >
                    <a-icon type="reload" />
                    重新兌換
                  </a-button>
                </div>
              </a-card>
            </div>
          </div>

          <!-- 分頁 -->
          <div v-if="totalRecords > pageSize" class="pagination-container">
            <a-card :bordered="false" style="background: linear-gradient(135deg, #f8fafc, #ffffff); border-radius: 12px; box-shadow: 0 2px 12px rgba(0,0,0,0.04); padding: 16px;">
              <div style="display: flex; justify-content: center; align-items: center; gap: 16px; flex-wrap: wrap;">
                <div style="color: #64748b; font-size: 14px; font-weight: 500;">
                  📊 第 {{ ((currentPage - 1) * pageSize) + 1 }}-{{ Math.min(currentPage * pageSize, totalRecords) }} 筆，共 {{ totalRecords }} 筆記錄
                </div>
                <a-pagination
                  v-model="currentPage"
                  :total="totalRecords"
                  :page-size="pageSize"
                  :show-size-changer="true"
                  :show-quick-jumper="true"
                  :show-total="() => ''"
                  @change="handlePageChange"
                  @showSizeChange="handlePageSizeChange"
                  style="margin: 0;"
                />
              </div>
            </a-card>
          </div>
        </div>
      </div>
    </div>
    <!-- 雲林幣兌換結果抽屜 -->
    <a-drawer
      placement="top"
      :closable="!processingRetry"
      :visible="drawerVisible"
      @close="onDrawerClose"
      :height="200"
      :mask="true"
      :maskClosable="!processingRetry"
      :zIndex="2000"
      :getContainer="false"
    >
      <div class="coin-result-content">
        <div class="coin-result-icon" :class="coinResultType">
          <a-icon :type="coinResultType === 'success' ? 'check-circle' : 'close-circle'" />
        </div>
        <div class="coin-result-message">
          <h3>{{ coinResultTitle }}</h3>
          <p>{{ coinResultMessage }}</p>
        </div>
      </div>
    </a-drawer>

    <!-- 自定義確認對話框 -->
    <a-modal
      v-model="confirmVisible"
      :title="confirmData?.title || '確認'"
      :width="confirmData?.width || 520"
      :confirmLoading="confirmLoading"
      :cancelButtonProps="{ disabled: confirmLoading }"
      :okButtonProps="{ disabled: confirmLoading }"
      @ok="handleConfirmOk"
      @cancel="handleConfirmCancel"
    >
      <div v-if="confirmData" v-html="confirmData.content"></div>
    </a-modal>

    <div v-if="viewname=='Myline'" style="padding:2rem;"></div>
    <Mybottommenu v-if="viewname=='Myline'" @savelove="savelove" :show="false" ref="con3" />    
    </a-spin>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { joinService } from '../_services';
import Mybottommenu from "../myline/panel/Mybottommenu.vue";
import * as moment from "moment/moment";
import router from '../router';
import store from "../store/index";

// Mock 假設的 API - 實際需要根據後端 API 調整
const queryAllOrders = (params) => {
  // 調用實際的後端 API
  return joinService.myallorders(params);
};

// 重新兌換 API
const retryExchangeApi = (tradeNo) => {
  // 調用實際的重新兌換 API
  return joinService.retryExchange(tradeNo);
};



export default {
  name: 'MyOrdersList',
  components: {
    Mybottommenu,
  },  
  data() {
    return {
      loading: true,
      orders: [],
      viewname:'',
      totalRecords: 0, // 總記錄數
      currentPage: 1,
      pageSize: 10,
      filters: {
        status: '',
        searchText: ''
      },
      // 新增統計資料
      statsData: {
        user_stats: { count: 0, total_point: 0 },
        trade_no_stats: { count: 0, total_point: 0 },
        success_stats: { count: 0, total_point: 0 },
        fail_stats: { count: 0, total_point: 0 }
      },
      // 雲林幣兌換結果抽屜控制
      drawerVisible: false,
      coinResultType: 'success', // 'success' 或 'error'
      coinResultTitle: '',
      coinResultMessage: '',
      // 執行中載入狀態
      processingRetry: false,
      // 確認對話框控制
      confirmVisible: false,
      confirmLoading: false,
      confirmData: null,
    };
  },
  computed: {
    ...mapState("account", ["status", "user"]),
    
    // 移除前端篩選，改為後端分頁
    paginatedOrders() {
      return this.orders; // 直接返回從 API 獲取的資料
    },
    
    successExchanges() {
      return this.statsData.success_stats.count;
    },
    
    failedExchanges() {
      return this.statsData.fail_stats.count;
    },
    
    totalPoints() {
      return this.statsData.success_stats.total_point;
    },
    
    // 新增計算屬性
    totalUserStats() {
      return this.statsData.user_stats;
    },
    
    tradeNoStats() {
      return this.statsData.trade_no_stats;
    },
    
    hasFilters() {
      return this.filters.status || this.filters.searchText;
    },
    
    // 檢查是否有失敗的兌換記錄
    hasFailedOrders() {
      return this.orders.some(order => order.states === 'N');
    },
    
    // 獲取所有失敗的兌換記錄
    failedOrders() {
      return this.orders.filter(order => order.states === 'N');
    }
  },
  mounted() {
    this.viewname = store.state.account.targetpage;
    this.fetchOrders();
  },
  methods: {
    async fetchOrders() {
      this.loading = true;
      try {
        // 準備 API 參數
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          uid: this.user?.data?.user?.id,
          mods: this.filters.status || 'all', // 如果沒有選擇狀態，預設為 'all'
          no: this.filters.searchText || 'all' // 如果沒有搜尋文字，預設為 'all'
        };
        
        const response = await queryAllOrders(params);
        
        // 處理 API 回應 - 根據新的雲林幣兌換資料結構
        if (response && response.data) {
          // 提取 trades 陣列作為訂單資料
          this.orders = response.data.trades || [];
          
          // 更新統計資料
          this.statsData = {
            user_stats: response.data.user_stats || { count: 0, total_point: 0 },
            trade_no_stats: response.data.trade_no_stats || { count: 0, total_point: 0 },
            success_stats: response.data.success_stats || { count: 0, total_point: 0 },
            fail_stats: response.data.fail_stats || { count: 0, total_point: 0 }
          };
          
          // 設定總記錄數（用於分頁）- 使用 user_stats 的 count 作為總數
          this.totalRecords = this.statsData.user_stats.count || this.orders.length;
        } else {
          // 如果回應格式不同，直接使用 response 作為資料陣列
          this.orders = response || [];
          this.totalRecords = this.orders.length;
          // 重置統計資料
          this.statsData = {
            user_stats: { count: 0, total_point: 0 },
            trade_no_stats: { count: 0, total_point: 0 },
            success_stats: { count: 0, total_point: 0 },
            fail_stats: { count: 0, total_point: 0 }
          };
        }
        
      } catch (error) {
        console.error('載入訂單失敗:', error);
        this.showCoinResult('error', '載入資料失敗', '❌ 載入訂單資料失敗\n🔧 請稍後再試或重新整理頁面');
        this.orders = [];
        this.totalRecords = 0;
      } finally {
        this.loading = false;
      }
    },
    
    getExchangeStatusColor(states) {
      return states === 'Y' ? 'green' : 'red';
    },
    
    getExchangeStatusText(states) {
      return states === 'Y' ? '✅ 兌換成功' : '❌ 兌換失敗';
    },
    
    fdateformat(val) {
      if (!val) return '未知';
      // 檢查是否為無效日期 0001-01-01
      if (val.toString().startsWith('0001-01-01')) return '';
      return moment(val).format("YYYY-MM-DD HH:mm");
    },
    
    formatMoney(amount) {
      if (!amount) return '0';
      return Number(amount).toLocaleString();
    },
    

    
    getPaymentMethodText(paymentMethod) {
      // 根據後端 paymentmethod 欄位
      if (paymentMethod === 'onsite' || paymentMethod === '現場付費') return '現場付費';
      if (paymentMethod === 'online' || paymentMethod === '線上付費') return '線上付費';
      if (paymentMethod === 'credit' || paymentMethod === '信用卡') return '信用卡';
      if (paymentMethod === 'atm' || paymentMethod === 'ATM') return 'ATM轉帳';
      if (paymentMethod === 'cvs' || paymentMethod === '超商代碼') return '超商代碼';
      return paymentMethod || '未指定';
    },
    
    getKindsText(kinds) {
      // 根據付款來源顯示文字
      if (kinds === 'web') return '官網';
      if (kinds === 'app') return '手機APP';
      if (kinds === 'admin') return '後台管理';
      if (kinds === 'line') return 'LINE';
      return kinds || '未知';
    },
    
    handleFilterChange() {
      this.currentPage = 1; // 重置到第一頁
      this.fetchOrders(); // 重新載入資料
    },
    
    clearFilters() {
      this.filters = {
        status: '',
        searchText: ''
      };
      this.currentPage = 1;
      this.fetchOrders(); // 重新載入資料
    },
    
    handlePageChange(page, pageSize) {
      this.currentPage = page;
      if (pageSize && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
      }
      this.fetchOrders(); // 載入新頁面的資料
    },
    
    handlePageSizeChange(current, size) {
      this.currentPage = 1;
      this.pageSize = size;
      this.fetchOrders(); // 重新載入資料
    },
    
    viewExchangeDetail(order) {
      // 顯示詳細的兌換資訊
      this.$info({
        title: '🪙 雲林幣兌換詳情',
        width: 600,
        content: (
          <div style="padding: 20px;">
            <div style="margin-bottom: 16px;">
              <strong>🎓 活動名稱：</strong>{order.actitle}
            </div>
            {order.fid > 0 && (
              <div style="margin-bottom: 16px;">
                <strong>🏢 課程編號：</strong>{order.fid}
              </div>
            )}
            {order.aid > 0 && (
              <div style="margin-bottom: 16px;">
                <strong>📋 活動編號：</strong>{order.aid}
              </div>
            )}
            <div style="margin-bottom: 16px;">
              <strong>📅 活動日期：</strong>{this.fdateformat(order.classtime)}
            </div>
            <div style="margin-bottom: 16px;">
              <strong>⏰ 兌換時間：</strong>{this.fdateformat(order.tradedate) || '待處理'}
            </div>
            <div style="margin-bottom: 16px;">
              <strong>🪙 兌換學分：</strong>{order.orderpoint} 分
            </div>
            <div style="margin-bottom: 16px;">
              <strong>📄 兌換單號：</strong>
              <span style="font-family: monospace; background: #f5f5f5; padding: 4px 8px; border-radius: 4px;">
                {order.merchant_trade_no}
              </span>
            </div>
            <div style="margin-bottom: 16px;">
              <strong>📊 兌換狀態：</strong>
              <span style={`color: ${order.states === 'Y' ? '#52c41a' : '#ff4d4f'}; font-weight: bold;`}>
                {order.states === 'Y' ? '✅ 兌換成功' : '❌ 兌換失敗'}
              </span>
            </div>
            {order.states === 'N' && order.error_message && (
              <div style="margin-bottom: 16px; padding: 12px; background: #fff2f0; border: 1px solid #ffccc7; border-radius: 6px;">
                <strong style="color: #cf1322;">❌ 失敗原因：</strong>
                <div style="color: #a8071a; margin-top: 8px;">{order.error_message}</div>
              </div>
            )}
          </div>
        ),
        okText: '關閉'
      });
    },


    retryAllFailedExchanges() {
      const failedCount = this.failedOrders.length;
      const totalFailedPoints = this.failedOrders.reduce((sum, order) => sum + (order.orderpoint || 0), 0);
      
      // 使用自定義確認對話框
      this.confirmData = {
        type: 'batch',
        title: '🔄 當前頁面批量重新兌換確認',
        width: 520,
        content: `
          <div style="padding: 16px;">
            <div style="margin-bottom: 16px; padding: 12px; background: #fff7e6; border: 1px solid #ffd591; border-radius: 6px;">
              <div style="font-size: 16px; font-weight: 600; color: #d46b08; margin-bottom: 8px;">
                ⚠️ 即將重新兌換當前頁面顯示的所有失敗記錄
              </div>
              <div style="color: #ad6800; margin-bottom: 4px;">
                • 當前頁面失敗記錄數量：<strong>${failedCount}</strong> 筆 (第 ${this.currentPage} 頁)
              </div>
              <div style="color: #ad6800;">
                • 涉及學分總計：<strong>${totalFailedPoints}</strong> 分
              </div>
            </div>
            <div style="color: #595959; font-size: 14px; line-height: 1.6;">
              <strong>注意：</strong>此操作僅針對當前頁面 (第 ${this.currentPage} 頁) 顯示的失敗記錄進行重新兌換。
              <br/>
              若您有其他頁面的失敗記錄，需要分別到各頁面執行批量重新兌換。
              <br/><br/>
              確定要重新嘗試兌換當前頁面的所有失敗記錄嗎？
              <br/>
              系統將逐一處理每筆失敗記錄，請耐心等待處理完成。
            </div>
          </div>
        `,
        okText: '🚀 確認重新兌換當前頁面',
        cancelText: '取消',
        okType: 'danger'
      };
      
      this.confirmVisible = true;
    },

    async retrySingleExchange(order) {
      // 使用自定義確認對話框
      this.confirmData = {
        type: 'single',
        order: order,
        title: '🔄 單筆重新兌換確認',
        width: 480,
        content: `
          <div style="padding: 16px;">
            <div style="margin-bottom: 16px; padding: 12px; background: #fff7e6; border: 1px solid #ffd591; border-radius: 6px;">
              <div style="font-size: 16px; font-weight: 600; color: #d46b08; margin-bottom: 8px;">
                🎓 ${order.actitle}
              </div>
              <div style="color: #ad6800; margin-bottom: 4px;">
                • 兌換單號：<strong>${order.merchant_trade_no}</strong>
              </div>
              <div style="color: #ad6800;">
                • 學分：<strong>${order.orderpoint}</strong> 分
              </div>
            </div>
            <div style="color: #595959; font-size: 14px; line-height: 1.6;">
              確定要重新嘗試兌換這筆失敗記錄嗎？
              <br/>
              系統將重新處理此筆兌換，請稍候片刻。
            </div>
          </div>
        `,
        okText: '🚀 確認重新兌換',
        cancelText: '取消',
        okType: 'primary'
      };
      
      this.confirmVisible = true;
    },

    // 顯示雲林幣兌換結果抽屜
    showCoinResult(type, title, message) {
      console.log('showCoinResult 被調用:', { type, title, message });
      this.coinResultType = type;
      this.coinResultTitle = title;
      this.coinResultMessage = message;
      this.drawerVisible = true;
      console.log('設定後的狀態:', {
        coinResultType: this.coinResultType,
        coinResultTitle: this.coinResultTitle,
        coinResultMessage: this.coinResultMessage,
        drawerVisible: this.drawerVisible
      });
      
      // 3秒後自動關閉
      setTimeout(() => {
        console.log('自動關閉抽屜');
        this.drawerVisible = false;
      }, 3000);
    },

    // 關閉抽屜
    onDrawerClose() {
      // 如果正在處理重新兌換，則不允許關閉
      if (this.processingRetry) {
        console.log('處理中，無法關閉抽屜');
        return;
      }
      this.drawerVisible = false;
    },

    // 處理確認對話框的確認按鈕
    async handleConfirmOk() {
      if (!this.confirmData) return;
      
      this.confirmLoading = true;
      
      try {
        if (this.confirmData.type === 'batch') {
          await this.processBatchRetry();
        } else if (this.confirmData.type === 'single') {
          await this.processSingleRetry(this.confirmData.order);
        }
      } catch (error) {
        console.error('處理確認操作失敗:', error);
      } finally {
        this.confirmLoading = false;
        this.confirmVisible = false;
        this.confirmData = null;
      }
    },

    // 處理確認對話框的取消按鈕
    handleConfirmCancel() {
      if (this.confirmLoading) {
        this.$message.warning('處理中無法取消，請等待完成');
        return;
      }
      
      this.confirmVisible = false;
      this.confirmData = null;
    },

    async processSingleRetry(order) {
      // 設置載入狀態
      this.processingRetry = true;
      
      try {
        // 呼叫實際的重新兌換 API
        await retryExchangeApi(order.merchant_trade_no);
        
        // 成功時顯示成功訊息
        this.showCoinResult('success', '雲林幣重新兌換成功', `🎉 恭喜您成功重新兌換雲林幣！\n單號：${order.merchant_trade_no}\n學分：${order.orderpoint} 分`);
        
        // 重新載入資料以更新狀態
        await this.fetchOrders();
        
      } catch (error) {
        console.error(`單筆重新兌換失敗 - ${order.merchant_trade_no}:`, error);
        
        // 使用 a-drawer 顯示錯誤訊息
        let errorMessage = '重新兌換失敗，請稍後再試或聯繫客服';
        
        // 根據後端返回的錯誤訊息調整顯示內容
        if (error.response && error.response.data) {
          if (error.response.data.msg) {
            errorMessage = error.response.data.msg;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        console.log('錯誤訊息:', errorMessage);
        this.showCoinResult('error', '雲林幣重新兌換失敗', "❌ 很抱歉，重新兌換雲林幣失敗了\n");
        
        // 不繼續執行任何後續操作，直接返回
        return;
      } finally {
        // 無論成功或失敗，都關閉載入狀態
        this.processingRetry = false;
      }
    },

    async processBatchRetry() {
      const failedOrders = this.failedOrders;
      let successCount = 0;
      let errorCount = 0;
      
      // 設置載入狀態
      this.processingRetry = true;
      
      try {
        for (let i = 0; i < failedOrders.length; i++) {
          const order = failedOrders[i];
          try {
            // 呼叫實際的重新兌換 API
            await retryExchangeApi(order.merchant_trade_no);
            
            successCount++;
            
          } catch (error) {
            console.error(`重新兌換失敗 - ${order.merchant_trade_no}:`, error);
            errorCount++;
          }
        }
        
        // 顯示處理結果
        if (successCount === failedOrders.length) {
          this.showCoinResult('success', '批量重新兌換完成', `🎉 恭喜！所有失敗記錄已成功重新兌換\n✅ 成功處理：${successCount} 筆記錄\n💰 總計學分：${failedOrders.reduce((sum, order) => sum + (order.orderpoint || 0), 0)} 分`);
        } else if (successCount > 0) {
          this.showCoinResult('success', '批量重新兌換部分成功', `⚠️ 部分記錄重新兌換成功\n✅ 成功：${successCount} 筆\n❌ 失敗：${errorCount} 筆\n💡 請檢查失敗記錄或稍後再試`);
        } else {
          this.showCoinResult('error', '批量重新兌換失敗', `❌ 很抱歉，所有記錄重新兌換都失敗了\n📝 失敗記錄：${errorCount} 筆\n🔧 請稍後再試或聯繫客服協助`);
        }
        
        // 重新載入資料以更新狀態
        await this.fetchOrders();
        
      } catch (error) {
        console.error('批量重新兌換錯誤:', error);
        this.showCoinResult('error', '批量重新兌換發生錯誤', '❌ 批量重新兌換過程中發生錯誤\n🔧 請稍後再試或聯繫客服協助');
      } finally {
        // 無論成功或失敗，都關閉載入狀態
        this.processingRetry = false;
      }
    },

    showStatsDetail() {
      // 顯示詳細統計資訊彈窗 - 使用 Modal 來解決 HTML 編碼問題
      const h = this.$createElement;
      
      const modal = this.$info({
        title: h('div', { 
          style: { 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            paddingRight: '20px' 
          } 
        }, [
          h('span', {}, '📊 詳細統計資訊'),
          h('a-button', {
            props: { type: 'text', size: 'small' },
            style: { 
              fontSize: '16px', 
              padding: '4px',
              color: '#999',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            },
            on: {
              click: () => {
                modal.destroy();
              }
            }
          }, [
            h('a-icon', { props: { type: 'close' } })
          ])
        ]),
        width: 1000,
        okText: '關閉',
        content: h('div', { style: { padding: '5px 15px 15px 5px' } }, [
          // 統計卡片網格 - 改為橫向排列
          h('div', { 
            style: { 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
              gap: '12px',
              marginLeft: '0px'
            } 
          }, [
            // 用戶總體統計
            h('div', {
              style: {
                background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #7dd3fc'
              }
            }, [
              h('h4', { 
                style: { 
                  color: '#0369a1', 
                  margin: '0 0 12px 0' 
                } 
              }, '👤 用戶總體統計'),
              h('div', {}, `兌換次數：${this.statsData.user_stats.count} 次`),
              h('div', {}, `涉及學分：${this.statsData.user_stats.total_point} 分`)
            ]),
            
            // 搜尋結果統計（條件顯示）
            this.tradeNoStats.count > 0 ? h('div', {
              style: {
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #f59e0b'
              }
            }, [
              h('h4', { 
                style: { 
                  color: '#d97706', 
                  margin: '0 0 12px 0' 
                } 
              }, '🔍 搜尋結果統計'),
              h('div', {}, `搜尋次數：${this.tradeNoStats.count} 次`),
              h('div', {}, `搜尋學分：${this.tradeNoStats.total_point} 分`)
            ]) : null,
            
            // 成功兌換統計
            h('div', {
              style: {
                background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #22c55e'
              }
            }, [
              h('h4', { 
                style: { 
                  color: '#16a34a', 
                  margin: '0 0 12px 0' 
                } 
              }, '✅ 成功兌換統計'),
              h('div', {}, `成功次數：${this.statsData.success_stats.count} 次`),
              h('div', {}, `獲得學分：${this.statsData.success_stats.total_point} 分`),
              h('div', { 
                style: { 
                  fontSize: '12px', 
                  color: '#15803d', 
                  marginTop: '8px' 
                } 
              }, `成功率：${((this.statsData.success_stats.count / (this.statsData.user_stats.count || 1)) * 100).toFixed(1)}%`)
            ]),
            
            // 失敗兌換統計
            h('div', {
              style: {
                background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #ef4444'
              }
            }, [
              h('h4', { 
                style: { 
                  color: '#dc2626', 
                  margin: '0 0 12px 0' 
                } 
              }, '❌ 失敗兌換統計'),
              h('div', {}, `失敗次數：${this.statsData.fail_stats.count} 次`),
              h('div', {}, `失敗學分：${this.statsData.fail_stats.total_point} 分`),
              h('div', { 
                style: { 
                  fontSize: '12px', 
                  color: '#b91c1c', 
                  marginTop: '8px' 
                } 
              }, `失敗率：${((this.statsData.fail_stats.count / (this.statsData.user_stats.count || 1)) * 100).toFixed(1)}%`)
            ])
          ].filter(Boolean)),
          
          // 整體績效區域
          h('div', {
            style: {
              marginTop: '16px',
              marginLeft: '0px',
              padding: '12px',
              background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
              borderRadius: '12px',
              border: '1px solid #94a3b8'
            }
          }, [
            h('h4', { 
              style: { 
                color: '#475569', 
                margin: '0 0 12px 0' 
              } 
            }, '📈 整體績效'),
            h('div', {
              style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '16px',
                textAlign: 'center'
              }
            }, [
              h('div', {}, [
                h('div', { 
                  style: { 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#059669' 
                  } 
                }, this.statsData.success_stats.total_point.toString()),
                h('div', { 
                  style: { 
                    fontSize: '12px', 
                    color: '#6b7280' 
                  } 
                }, '已獲得學分')
              ]),
              h('div', {}, [
                h('div', { 
                  style: { 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#dc2626' 
                  } 
                }, this.statsData.fail_stats.total_point.toString()),
                h('div', { 
                  style: { 
                    fontSize: '12px', 
                    color: '#6b7280' 
                  } 
                }, '失敗學分')
              ]),
              h('div', {}, [
                h('div', { 
                  style: { 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#7c3aed' 
                  } 
                }, this.statsData.user_stats.total_point.toString()),
                h('div', { 
                  style: { 
                    fontSize: '12px', 
                    color: '#6b7280' 
                  } 
                }, '總計學分')
              ])
            ])
          ])
        ])
      });
    },




    goHome() {
      // 檢查用戶來源，智能返回
      const fromRoute = this.$route.query.from || '';
      let names = store.state.account.targetpage;
      
      // 如果明確從首頁來，或者沒有特定的 targetpage，直接返回首頁
      if (fromRoute === 'home' || fromRoute === '/' || !names || names === '') {
        router.replace('/').catch(err => console.error(err));
      } else if (names === "Myline") {
        router.replace({ name: 'Myline' }).catch(err => console.error(err));
      } else {
        // 預設返回首頁
        router.replace('/').catch(err => console.error(err));
      }
    }, 
    
    savelove(val) {
        if (val == "home") {
          router.replace({ name: "Myline" });
        } else if(val == "score"){
          router.replace({ name: "MyScoreData" });
        } else if (val == "act") {
          router.replace({ name: "Mydoing" });
        } else if(val == "love"){
          router.replace({ name: "Mylove" });
        }     
    },     
  }
};
</script>

<style scoped>
/* 主要容器 */
.my-orders-page {
  background: #f5f5f5;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.page-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* 頂部導航 */
.top-navigation {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-button {
  color: #666;
  font-weight: 500;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background: #f0f0f0;
  color: #333;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.nav-spacer {
  width: 80px;
}

/* 內容包裝器 */
.content-wrapper {
  flex: 1;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

/* 篩選區域 */
.filter-section {
  margin-bottom: 24px;
}

.filter-card {
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.filter-controls {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

/* 載入和空狀態 */
.loading-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
}

.loading-text {
  margin-top: 20px;
  color: #666;
  font-size: 16px;
  font-weight: 500;
}

.empty-content {
  text-align: center;
  max-width: 400px;
}

.empty-icon {
  font-size: 4rem;
  color: #d1d5db;
  margin-bottom: 24px;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.empty-message {
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 32px;
}

.empty-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

/* 統計卡片 */
.statistics-card {
  margin-bottom: 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
}

.stat-item {
  text-align: center;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  border: 1px solid #dee2e6;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 8px;
}

.stat-value.success-stat {
  color: #52c41a;
}

.stat-value.failed-stat {
  color: #ff4d4f;
}

.stat-value.points-stat {
  color: #fa8c16;
}

.stat-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 訂單列表 */
.orders-list {
  display: grid;
  gap: 20px;
}

.order-card {
  opacity: 0;
  transform: translateY(20px);
  animation: slideInUp 0.6s ease forwards;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.order-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

@keyframes slideInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.order-item {
  border: none;
  border-radius: 16px;
}

/* 訂單標題區域 */
.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.order-info {
  flex: 1;
}

.order-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.order-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.9rem;
  color: #6c757d;
}

.order-meta-left {
  align-items: flex-start;
}

.order-number {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  background: #f8f9fa;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}

.order-source {
  background: #e3f2fd;
  color: #1976d2;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
}

.order-status {
  flex-shrink: 0;
}

/* 訂單內容 */
.order-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin: 16px 0;
}

.order-details {
  display: grid;
  gap: 8px;
}

.detail-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 0.9rem;
}

.detail-row.error-message {
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-weight: 600;
  color: #495057;
  min-width: 80px;
  flex-shrink: 0;
}

.detail-value {
  color: #2c3e50;
  line-height: 1.4;
}

/* 金額區域 */
.order-amount {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #dee2e6;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 0.9rem;
}

.amount-row.fee {
  color: #dc3545;
  font-size: 0.85rem;
}

.amount-row.total {
  border-top: 2px solid #495057;
  margin-top: 8px;
  padding-top: 12px;
  font-weight: 700;
  font-size: 1rem;
  color: #667eea;
}

.amount-label {
  font-weight: 500;
}

.amount-value {
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

/* 雲林幣兌換相關樣式 */
.points-badge {
  background: linear-gradient(135deg, #ffa940, #fa8c16);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  margin-bottom: 8px;
  display: inline-block;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(250, 140, 22, 0.3);
}

.points-text {
  font-size: 14px;
}

.exchange-status {
  font-weight: 600 !important;
}

.exchange-summary {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.summary-card.success-card {
  background: linear-gradient(135deg, #f6ffed, #d9f7be);
  border: 1px solid #b7eb8f;
}

.summary-card.failed-card {
  background: linear-gradient(135deg, #fff2f0, #ffccc7);
  border: 1px solid #ffa39e;
}

.summary-icon {
  font-size: 28px;
}

.summary-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-status {
  font-weight: 600;
  font-size: 16px;
}

.summary-points {
  font-size: 24px;
  font-weight: 700;
  color: #fa8c16;
}

.success-card .summary-status {
  color: #389e0d;
}

.failed-card .summary-status {
  color: #cf1322;
}

.error-message .detail-label {
  color: #cf1322;
}

.error-text-wrapper {
  flex: 1;
  margin-top: 4px;
}

.error-text {
  color: #cf1322;
  font-weight: 500;
  background: #fff2f0;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #ffccc7;
  display: block;
  line-height: 1.6;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(207, 19, 34, 0.1);
}

/* 操作按鈕 */
.order-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

/* 統一操作按鈕樣式 */
.order-actions .action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  letter-spacing: 0.5px;
  border: none;
  background: #f0f3f8;
  color: #394b5a;
  padding: 8px 14px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  transition: all .25s ease;
}
.order-actions .action-btn .anticon { font-size: 14px; }
.order-actions .action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0,0,0,0.15);
  background: #e6ebf2;
}
.order-actions .action-btn:active { transform: translateY(0); }

/* 詳情 */
.order-actions .action-btn--detail {
  background: linear-gradient(135deg,#eef2ff,#e0e7ff);
  color: #394b8a;
}
.order-actions .action-btn--detail:hover { background: linear-gradient(135deg,#e0e7ff,#c7d2fe); }

/* 付款 */
.order-actions .action-btn--pay {
  background: linear-gradient(135deg,#d1fae5,#a7f3d0);
  color: #065f46;
}
.order-actions .action-btn--pay:hover { background: linear-gradient(135deg,#a7f3d0,#6ee7b7); }

/* 重新兌換 */
.order-actions .action-btn--retry {
  background: linear-gradient(135deg,#fff7ed,#fed7aa);
  color: #c2410c;
}
.order-actions .action-btn--retry:hover { background: linear-gradient(135deg,#fed7aa,#fdba74); }

@media (max-width: 768px) {
  .order-actions .action-btn { flex: 1; justify-content: center; }
}

/* 分頁 */
.pagination-container {
  margin-top: 32px;
}

/* 統計標題按鈕動效 */
.stats-header .ant-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stats-header .ant-btn:hover {
  transform: translateY(-2px);
}

.stats-header .ant-btn[type="primary"]:hover {
  box-shadow: 0 6px 20px rgba(24, 144, 255, 0.4) !important;
}

.stats-header .ant-btn[type="danger"]:hover {
  box-shadow: 0 6px 20px rgba(255, 77, 79, 0.4) !important;
}

/* 載入和空狀態動畫 */
.loading-container .ant-card,
.empty-container .ant-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 美化按鈕樣式 */
.ant-btn-lg {
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.ant-btn-lg:hover {
  transform: translateY(-2px);
}

/* 響應式設計 */
@media (max-width: 768px) {
  .content-wrapper {
    padding: 16px;
  }
  
  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-controls > * {
    width: 100% !important;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
  
  .order-header {
    flex-direction: column;
    gap: 12px;
  }
  
  .order-content {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .order-actions {
    flex-direction: column;
  }
  
  .order-actions .ant-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .top-navigation {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 1.3rem;
  }
}

/* 雲林幣兌換結果抽屜樣式 */
.coin-result-content {
  display: flex;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 8px;
  margin: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2001;
}

.coin-result-icon {
  font-size: 48px;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
}

.coin-result-icon.success {
  color: #52c41a;
  background: rgba(82, 196, 26, 0.1);
}

.coin-result-icon.error {
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.1);
}

.coin-result-message h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: bold;
}

.coin-result-message p {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.4;
}

/* 執行中載入樣式 */
:deep(.processing-spin-wrapper) {
  min-height: 100vh;
}

:deep(.processing-spin-wrapper .ant-spin-container) {
  opacity: 0.6;
  pointer-events: none;
}

:deep(.processing-spin-wrapper .ant-spin) {
  background: rgba(255, 255, 255, 0.9);
}

:deep(.processing-spin-wrapper .ant-spin-text) {
  color: #1890ff;
  font-weight: 600;
  font-size: 16px;
}

/* 處理中的抽屜樣式 */
:deep(.ant-drawer-mask) {
  pointer-events: auto !important;
}

:deep(.ant-drawer-content) {
  pointer-events: auto !important;
}

:deep(.ant-drawer-close) {
  opacity: 0.3 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
}

/* 禁用按鈕樣式 */
.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ant-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
